import type { FlashList } from '@shopify/flash-list';
import type { SessionOutput } from '@sunrise-ui/api/events/api';
import { Colors } from '@sunrise-ui/primitives';
import { DateTime } from 'luxon';
import type { RefObject } from 'react';
import type { MarkedDates } from 'react-native-calendars/src/types';

export type SessionSection = {
  startDateTime: string; // ISO date string with full datetime info
  sessions: SessionOutput[];
};

export type FlashListItem =
  | { type: 'header'; startDateTime: string; sectionIndex: number }
  | {
      type: 'section-list';
      sessions: SessionOutput[];
      sectionIndex: number;
    };

export const createSessionMarkedDates = (
  sessions: SessionOutput[] | undefined,
): MarkedDates => {
  if (!sessions) return {};

  const marked: MarkedDates = {};

  for (const session of sessions) {
    const sessionDate = DateTime.fromISO(session.start_date!);
    const dateKey = sessionDate.toISODate();
    if (!dateKey) continue;

    const dotColor = Colors.pink0;

    if (!marked[dateKey]) {
      marked[dateKey] = {
        marked: true,
        dotColor,
        dots: [{ color: dotColor }],
      };
    } else {
      const dots = marked[dateKey].dots ?? [];
      const hasColor = dots.some((d) => d.color === dotColor);
      if (!hasColor && dots.length < 4) {
        marked[dateKey] = {
          ...marked[dateKey],
          dots: [...dots, { color: dotColor }],
        };
      }
    }
  }

  return marked;
};

export const groupSessionsByDate = (
  sessions: SessionOutput[] | undefined,
  sortDirection: 'asc' | 'desc' = 'asc',
): SessionSection[] => {
  if (!sessions) return [];

  const groups: Record<string, SessionOutput[]> = {};

  sessions.forEach((session) => {
    const sessionDateTime = DateTime.fromISO(session.start_date!);
    if (sessionDateTime.isValid) {
      // Use the full ISO string as the group key to maintain all datetime info
      const groupKey = sessionDateTime.toISO();

      if (!groups[groupKey]) {
        groups[groupKey] = [];
      }
      groups[groupKey].push(session);
    }
  });

  return Object.entries(groups)
    .map(([startDateTime, sessions]) => ({
      startDateTime,
      sessions,
    }))
    .sort((a, b) => {
      const dateTimeA = DateTime.fromISO(a.startDateTime);
      const dateTimeB = DateTime.fromISO(b.startDateTime);
      const comparison = dateTimeA.toMillis() - dateTimeB.toMillis();
      return sortDirection === 'desc' ? -comparison : comparison;
    });
};

// FlashList-specific grouping function for masonry layout
export const createFlashListData = (
  groupedSessions: SessionSection[],
): FlashListItem[] => {
  const items: FlashListItem[] = [];

  groupedSessions.forEach(({ startDateTime, sessions }, sectionIndex) => {
    // Add section header (full width)
    items.push({
      type: 'header',
      startDateTime,
      sectionIndex,
    });

    // Add row with all sessions from this section
    items.push({
      type: 'section-list',
      sessions: sessions,
      sectionIndex,
    });
  });

  return items;
};

// Find nearest section index for FlashList scrolling
export const findNearestFlashListSectionIndex = (
  selectedDate: string,
  groupedSessions: SessionSection[],
  sortDirection: 'asc' | 'desc' = 'asc',
): number => {
  const isAsc = sortDirection === 'asc';
  const selectedDateTime = DateTime.fromISO(selectedDate);
  let nearestSectionIndex = -1;

  for (const [index, section] of groupedSessions.entries()) {
    const sectionDate = DateTime.fromISO(section.sessions[0].start_date!);
    const shiftedDate = isAsc
      ? sectionDate.endOf('day')
      : sectionDate.startOf('day');

    if (
      isAsc ? shiftedDate < selectedDateTime : shiftedDate > selectedDateTime
    ) {
      nearestSectionIndex = index;
    } else {
      nearestSectionIndex = Math.max(0, nearestSectionIndex + 1);
      break;
    }
  }

  return nearestSectionIndex;
};

type ScrollToFlashListDateParams = {
  selectedDate: string;
  flashListRef: RefObject<FlashList<FlashListItem>>;
  groupedSessions: SessionSection[];
  viewableItemIndicesRef: RefObject<number[]>;
  sortDirection?: 'asc' | 'desc';
};

// Scroll to date in FlashList
export const scrollToFlashListDate = ({
  selectedDate,
  flashListRef,
  groupedSessions,
  viewableItemIndicesRef,
  sortDirection = 'asc',
}: ScrollToFlashListDateParams) => {
  if (!flashListRef.current) return;

  const nearestSectionIndex = findNearestFlashListSectionIndex(
    selectedDate,
    groupedSessions,
    sortDirection,
  );

  // With nested FlashList structure: each section = 1 header + 1 section
  // So target index is simply: nearestSectionIndex * 2 (to get to the header)
  let targetIndex = -1;
  if (
    nearestSectionIndex >= 0 &&
    nearestSectionIndex < groupedSessions.length
  ) {
    targetIndex = nearestSectionIndex * 2; // Each section contributes exactly 2 items: header + masonry-row
  }

  if (targetIndex >= 0) {
    flashListRef.current?.scrollToIndex({
      index: targetIndex,
      animated: true,
      viewPosition: 0,
    });
    setTimeout(() => {
      const viewableItemIndex = viewableItemIndicesRef?.current ?? [
        targetIndex,
      ];
      if (!viewableItemIndex.includes(targetIndex)) {
        scrollToFlashListDate({
          selectedDate,
          flashListRef,
          groupedSessions,
          viewableItemIndicesRef,
          sortDirection,
        });
      }
    }, 500);
  }
};
